/**
 * Sign-in API helpers
 * Handles authentication API calls (Supabase Auth).
 */

import { Platform } from 'react-native';
import { PRODUCTION_APP_ORIGIN } from '../../../constants/env';
import { NATIVE_OAUTH_REDIRECT_URL, NATIVE_OAUTH_REDIRECT_COMPLETE } from '../constants/oauth.constants';
import type { AuthError } from '../types/signIn.types';
import { TURNSTILE_VERIFY_FAILED_MESSAGE } from '../../auth/turnstile/turnstile.constants';
import { reportNativeError } from '../../shared/helpers/nativeCrashReporting.helper';
import { isTransientAuthError, TRANSIENT_AUTH_ERROR_MESSAGE } from './transientAuthError.helper';

export type ShowAlertFn = (options: { title: string; message: string }) => Promise<boolean>;

/**
 * Extract error message from auth error
 */
export function getErrorMessage(error: unknown, fallback: string): string {
  const err = error as AuthError;
  return err?.errors?.[0]?.message || err?.message || fallback;
}

/**
 * Show authentication error via app dialog system.
 * Caller must pass showAlert from useDialog().
 * Transient/unmapped errors are reported to crash reporting (AC-2.9) — mapped user
 * errors (wrong password, rate limit, …) are expected and stay local.
 */
export async function showAuthError(
  error: unknown,
  context: string,
  showAlert: ShowAlertFn
): Promise<void> {
  const message = getUserFriendlyErrorMessage(error);
  const rawMessage = (error as { message?: string })?.message;
  const isUnmapped =
    message === GENERIC_AUTH_ERROR_MESSAGE ||
    message === TRANSIENT_AUTH_ERROR_MESSAGE ||
    (rawMessage !== undefined && message === rawMessage);
  if (isUnmapped) {
    const reportable = error instanceof Error ? error : new Error(String(rawMessage ?? error));
    reportNativeError(reportable, { authFlow: context });
  }
  await showAlert({ title: 'Error', message });
}

/**
 * Validate email format
 */
export function validateEmailFormat(email: string): { isValid: boolean; error?: string } {
  if (!email || email.trim() === '') {
    return { isValid: false, error: 'Email is required' };
  }

  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  return { isValid: true };
}

/** Max password length — prevents DoS via expensive bcrypt computation on auth provider */
export const MAX_PASSWORD_LENGTH = 64;

/**
 * Validate password
 */
export function validatePassword(password: string, isSignUp: boolean): { isValid: boolean; error?: string } {
  if (!password || password.trim() === '') {
    return { isValid: false, error: 'Password is required' };
  }

  if (password.length > MAX_PASSWORD_LENGTH) {
    return { isValid: false, error: `Password must be ${MAX_PASSWORD_LENGTH} characters or fewer` };
  }

  if (isSignUp) {
    if (password.length < 8) {
      return { isValid: false, error: 'Password must be at least 8 characters' };
    }
  }

  return { isValid: true };
}

/**
 * Validate credentials and return detailed results
 */
export function validateCredentials(email: string, password: string, isSignUp: boolean = false): {
  isValid: boolean;
  emailError?: string;
  passwordError?: string;
} {
  const emailValidation = validateEmailFormat(email);
  const passwordValidation = validatePassword(password, isSignUp);

  return {
    isValid: emailValidation.isValid && passwordValidation.isValid,
    emailError: emailValidation.error,
    passwordError: passwordValidation.error,
  };
}

/** Final fallback when no specific mapping matches and the error carries no message. */
export const GENERIC_AUTH_ERROR_MESSAGE = 'An error occurred. Please try again.';

/**
 * Get user-friendly error message from auth error (Supabase or generic shape)
 */
export function getUserFriendlyErrorMessage(error: unknown): string {
  const err = error as { errors?: Array<{ code?: string; message?: string; longMessage?: string }>; message?: string; code?: string };
  const message = (err?.message ?? '').toLowerCase();

  // Supabase Auth errors (message-based)
  const supabaseMessageMap: Record<string, string> = {
    'invalid login credentials': 'No account found with this email or incorrect password',
    'token has expired or is invalid': 'That code is incorrect or has expired. Tap “Resend code” to get a new one.',
    'otp_expired': 'That code has expired. Tap “Resend code” to get a new one.',
    'email not confirmed': 'Please confirm your email address before signing in',
    'user already registered': 'An account with this email already exists',
    'signup requires a valid password': 'Password does not meet requirements',
    'password should be at least 6 characters': 'Password must be at least 6 characters',
    'email rate limit exceeded': 'Too many attempts. Please wait a few minutes and try again.',
    'rate limit exceeded': 'Too many attempts. Please wait a few minutes and try again.',
    'captcha protection: verification process failed': TURNSTILE_VERIFY_FAILED_MESSAGE,
    'captcha verification process failed': TURNSTILE_VERIFY_FAILED_MESSAGE,
    'invalid-input-response': TURNSTILE_VERIFY_FAILED_MESSAGE,
    /** Turnstile siteverify: wrong/malformed secret in Supabase Auth captcha settings (not a client bug). */
    'invalid-input-secret': TURNSTILE_VERIFY_FAILED_MESSAGE,
    'request disallowed': TURNSTILE_VERIFY_FAILED_MESSAGE,
  };
  for (const [key, friendly] of Object.entries(supabaseMessageMap)) {
    if (message.includes(key)) return friendly;
  }

  // Generic auth error shape (e.g. errors[0].code)
  const authError = err?.errors?.[0];
  const code = authError?.code || err?.code || '';
  const codeMap: Record<string, string> = {
    captcha_failed: TURNSTILE_VERIFY_FAILED_MESSAGE,
    'form_identifier_not_found': 'No account found with this email',
    'form_password_incorrect': 'Incorrect password',
    'form_identifier_exists': 'An account with this email already exists',
    'form_password_pwned': 'This password is too common and easy to guess. Please choose a unique one.',
    'form_password_length_too_short': 'Password must be at least 8 characters',
    'form_param_format_invalid': 'Invalid email format',
    'form_password_policy_violation': 'Password does not meet requirements',
  };
  if (code && codeMap[code]) return codeMap[code];

  // Transient fetch/parse failures (HTML body from a CDN, dropped connection): friendly
  // retryable copy instead of raw "JSON Parse error…" / "Network request failed" (AC-2.8).
  if (isTransientAuthError(error)) return TRANSIENT_AUTH_ERROR_MESSAGE;

  return authError?.longMessage || authError?.message || err?.message || GENERIC_AUTH_ERROR_MESSAGE;
}

/**
 * Maps auth error (Supabase or generic) to form field for inline error display
 */
export function mapAuthErrorToField(error: unknown): 'email' | 'password' | 'code' | 'general' {
  const err = error as { errors?: Array<{ code?: string }>; message?: string; code?: string };
  const message = (err?.message ?? '').toLowerCase();
  const authError = err?.errors?.[0];
  const code = (authError?.code || err?.code || '').toLowerCase();

  if (code) {
    if (code.includes('identifier') || code.includes('email') || code.includes('not_found') || code.includes('exists')) return 'email';
    if (code.includes('password') || code.includes('incorrect')) return 'password';
    if (code.includes('code') || code.includes('verification')) return 'code';
  }
  if (message.includes('email') && (message.includes('confirm') || message.includes('not found') || message.includes('invalid'))) return 'email';
  if (message.includes('rate limit')) return 'email'; // Supabase email rate limit → show under email field
  if (message.includes('password') || message.includes('invalid login')) return 'password';
  if (message.includes('code') || message.includes('otp') || message.includes('verification')) return 'code';
  return 'general';
}

/**
 * Create sign up with timeout protection
 * CAPTCHA/Turnstile can sometimes hang on slow cellular networks
 * 30 seconds allows time for users to solve manual CAPTCHA puzzles
 */
export async function signUpWithTimeout<T>(
  signUpPromise: Promise<T>,
  timeoutMs: number = 50000 // 50 seconds
): Promise<T> {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(
        new Error(
          'Sign up request timed out. This might be due to CAPTCHA verification. Please try again.'
        )
      );
    }, timeoutMs);
  });

  return Promise.race([signUpPromise, timeoutPromise]);
}

/**
 * Setup CAPTCHA element for web platform (if required by auth provider).
 * No-op for Supabase Auth; kept for compatibility.
 */
export function setupCaptchaElement(): void {
  // No-op: Supabase Auth does not require manual CAPTCHA setup
}

/**
 * Get OAuth redirect URLs for web and native.
 * Web: uses EXPO_PUBLIC_APP_URL when set (e.g. your IP for phone testing), so OAuth redirects back there.
 *      When not set, uses current origin (localhost on computer, or whatever you opened).
 *      When on production domain, uses PRODUCTION_APP_ORIGIN.
 * Native: returns deep-link URLs (zporti://) so the app receives the OAuth callback.
 */
/**
 * Resolves the canonical app base URL for auth redirects.
 * On native: returns the production origin (email links open in browser, not the native app).
 * On web: uses window.location.origin, but always uses PRODUCTION_APP_ORIGIN when running on the production domain.
 * Returns null when window is unavailable (SSR/build).
 */
function getAppBaseUrl(): string | null {
  if (Platform.OS !== 'web') {
    return PRODUCTION_APP_ORIGIN;
  }
  if (typeof window === 'undefined') {
    return null;
  }
  const origin = window.location.origin;
  const isOnProductionSite =
    PRODUCTION_APP_ORIGIN && (origin === PRODUCTION_APP_ORIGIN || origin.startsWith(PRODUCTION_APP_ORIGIN + ':'));
  return isOnProductionSite ? PRODUCTION_APP_ORIGIN : origin;
}

export function getOAuthRedirectUrls(): { redirectUrl: string; redirectUrlComplete: string } | null {
  if (Platform.OS !== 'web') {
    return {
      redirectUrl: NATIVE_OAUTH_REDIRECT_URL,
      redirectUrlComplete: NATIVE_OAUTH_REDIRECT_COMPLETE,
    };
  }

  const base = getAppBaseUrl();
  if (!base) return null;

  return {
    redirectUrl: base + '/oauth-native-callback',
    redirectUrlComplete: base + '/app/',
  };
}

/**
 * Get the redirect URL for password reset emails.
 */
export function getPasswordResetRedirectUrl(): string {
  return (getAppBaseUrl() ?? PRODUCTION_APP_ORIGIN) + '/reset-password';
}

/**
 * Parse Supabase OAuth redirect URL and return tokens for setSession.
 * Supabase sends access_token and refresh_token in the hash fragment.
 * Returns null if the URL does not contain both tokens.
 */
export function parseSupabaseOAuthRedirectUrl(url: string | null): {
  access_token: string;
  refresh_token: string;
} | null {
  if (!url || typeof url !== 'string') return null;
  const hashIdx = url.indexOf('#');
  const queryIdx = url.indexOf('?');
  const fragment = hashIdx >= 0 ? url.slice(hashIdx + 1) : '';
  const query = queryIdx >= 0 ? url.slice(queryIdx + 1, hashIdx >= 0 ? hashIdx : undefined) : '';
  const params = new URLSearchParams(fragment || query);
  const access_token = params.get('access_token');
  const refresh_token = params.get('refresh_token');
  if (access_token && refresh_token) {
    return { access_token, refresh_token };
  }
  return null;
}

